import React, { useImperativeHandle, useMemo, forwardRef, useState } from 'react';
import FormField from '../forms/FormField';
import RichTextContent from '../forms/RichTextContent';
import { stripHtmlToText } from '../../utils/richText';
import { isPointsForm } from '../../utils/formScoring';
import { isFormFileAnswerEmpty } from '../../utils/formFileUploads';
import './AssignmentFormCanvas.css';

function groupQuestions(questions) {
  const groups = [];
  const map = new Map();
  (questions || []).forEach((q, index) => {
    const name = (q.group && String(q.group).trim()) || '';
    if (!map.has(name)) {
      const entry = { name, questions: [] };
      map.set(name, entry);
      groups.push(entry);
    }
    map.get(name).questions.push({ ...q, displayIndex: index + 1 });
  });
  return groups;
}

function isEmptyAnswer(value, field) {
  if (value == null || value === '') return true;
  if (field?.type === 'file-upload') {
    return isFormFileAnswerEmpty(value, field.allowMultiple !== false);
  }
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object' && !Array.isArray(value) && !value.name && !value.documentId) {
    return true;
  }
  return false;
}

function buildValidationErrors(form, formData) {
  const next = {};
  (form?.questions || []).forEach((field) => {
    const questionId = field.question_id;
    if (!questionId || !field.required) return;
    if (isEmptyAnswer(formData[questionId], field)) {
      const labelText = stripHtmlToText(field.label) || 'This field';
      next[questionId] = `${labelText} is required`;
    }
  });
  return next;
}

const AssignmentFormCanvas = forwardRef(function AssignmentFormCanvas(
  {
    form,
    formData = {},
    onFormDataChange,
    readOnly = false,
    isSubmitting = false,
    hideTitleInHero = false,
    onPendingFileRegister,
    onPendingFileRelease,
    documentUrlById = {},
    scoringFeedback = null
  },
  ref
) {
  const [errors, setErrors] = useState({});

  useImperativeHandle(ref, () => ({
    validate() {
      const next = buildValidationErrors(form, formData);
      setErrors(next);
      return Object.keys(next).length === 0;
    }
  }));

  const sections = useMemo(() => groupQuestions(form?.questions), [form?.questions]);
  const pointsForm = isPointsForm(form?.type);
  let questionCounter = 0;

  const handleFieldChange = (questionId, value) => {
    if (readOnly || !onFormDataChange) return;
    const next = { ...formData, [questionId]: value };
    onFormDataChange(next);
    if (errors[questionId]) {
      setErrors((prev) => ({ ...prev, [questionId]: null }));
    }
  };

  if (!form?.questions?.length) {
    return <p className="afc-empty">This form has no questions.</p>;
  }

  return (
    <div className="afc">
      <div className="afc-scroll">
        <div className="afc-stack">
          {(form.description || (form.title && !hideTitleInHero)) && (
            <header className="afc-hero">
              {form.title && !hideTitleInHero ? (
                <h3 className="afc-hero__title">{form.title}</h3>
              ) : null}
              {form.description ? (
                <RichTextContent html={form.description} className="afc-hero__desc" as="div" />
              ) : null}
            </header>
          )}

          {sections.map((section) => (
            <section key={section.name || '__default'} className="afc-section">
              {section.name ? (
                <h4 className="afc-section__title">{section.name}</h4>
              ) : null}
              <div className="afc-section__questions">
                {section.questions.map((field) => {
                  questionCounter += 1;
                  const qNum = questionCounter;
                  const questionId = field.question_id;
                  const pts = pointsForm && field.points != null ? Number(field.points) : null;
                  const feedback = scoringFeedback?.[questionId];
                  const pointsOnly = feedback?.pointsOnly === true;
                  const neutral = feedback?.neutral === true;
                  const feedbackClass =
                    feedback && !pointsOnly
                      ? feedback.pending
                        ? ' afc-question--pending'
                        : feedback.correct
                          ? ' afc-question--correct'
                          : neutral
                            ? ''
                            : ' afc-question--wrong'
                      : '';
                  const resultClass =
                    feedback && !pointsOnly
                      ? feedback.pending
                        ? ' afc-question__result--pending'
                        : feedback.correct
                          ? ' afc-question__result--correct'
                          : neutral
                            ? ' afc-question__result--score'
                            : ' afc-question__result--wrong'
                      : pointsOnly
                        ? ' afc-question__result--score'
                        : '';
                  const resultLabel =
                    feedback && !pointsOnly && !neutral
                      ? feedback.pending
                        ? 'Pending'
                        : feedback.correct
                          ? 'Correct'
                          : 'Incorrect'
                      : '';
                  const compactPoints = pointsOnly || neutral;
                  const resultPoints = feedback
                    ? feedback.pending
                      ? Number.isFinite(feedback.max)
                        ? compactPoints
                          ? `out of ${feedback.max} pt`
                          : ` · out of ${feedback.max} pt`
                        : ''
                      : Number.isFinite(feedback.earned) && Number.isFinite(feedback.max)
                        ? compactPoints
                          ? `${feedback.earned}/${feedback.max} pt`
                          : ` · ${feedback.earned}/${feedback.max} pt`
                        : ''
                    : '';
                  const resultText = compactPoints ? resultPoints : `${resultLabel}${resultPoints}`;

                  return (
                    <article
                      key={questionId || field.displayIndex}
                      className={`afc-question${errors[questionId] ? ' afc-question--error' : ''}${
                        readOnly ? ' afc-question--readonly' : ''
                      }${feedbackClass}`}
                      aria-required={field.required ? true : undefined}
                    >
                      <div className="afc-question__inner">
                        <div className="afc-question__index-wrap">
                          <span className="afc-question__num" aria-hidden>
                            {qNum}
                          </span>
                          {field.required ? (
                            <span className="afc-question__required" title="Required" aria-hidden>
                              *
                            </span>
                          ) : null}
                        </div>
                        <header className="afc-question__head">
                          <div className="afc-question__label-wrap">
                            <RichTextContent
                              html={field.label}
                              className="afc-question__label"
                              as="div"
                            />
                          </div>
                          {pts != null && Number.isFinite(pts) ? (
                            <span className="afc-question__points">{pts} pt</span>
                          ) : null}
                          {feedback && resultText ? (
                            <span className={`afc-question__result${resultClass}`}>
                              {resultText}
                            </span>
                          ) : null}
                        </header>
                        <div className="afc-question__body">
                          <FormField
                            field={field}
                            value={formData[questionId]}
                            onChange={handleFieldChange}
                            error={errors[questionId]}
                            variant="canvas"
                            hideLabel
                            readOnly={readOnly || isSubmitting}
                            onPendingFileRegister={onPendingFileRegister}
                            onPendingFileRelease={onPendingFileRelease}
                            documentUrlById={documentUrlById}
                          />
                        </div>
                        {errors[questionId] ? (
                          <p className="afc-question__error" role="alert">
                            {errors[questionId]}
                          </p>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
});

export default AssignmentFormCanvas;
export { isEmptyAnswer, buildValidationErrors };
